import { useEffect, useState, useMemo, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common/NewSelectMultiSelect';
import { ClearIcon, FilterIcon, SaveIcon } from '../../../../bombiix-icon';
import {
  cloneData,
  convertSelectLabel,
  currencyCodes,
  Page_Size,
  checkArray,
  createOptions,
  getYears,
  months,
  CountriesList,
  getCurrentYearLabel,
} from '../../../../Components/helper';
import {
  fetchAllBrandAction,
  filterSortData,
  getCostingList,
  getCostingStats,
  getSupplierBrands,
  getSuppliers,
  getUserSuppliers,
} from './store/Costing.action';
import { fetchColorLibrary } from '../../../Library/ColorLibary/store/ColorLibrary.action';
import {
  clearLicenseData,
  clearLikenessData,
  getCatBasedOnPT,
  getIncotermsDetails,
  getLicenseDetails,
  getLicensorAccess,
  getLicensorDetails,
  getLikenessDetails,
  getMultiSubBrandDetails,
  getSubBrandAccess,
  getTypeBasedCatgeory,
} from '../../../SystemMasters/Store/SystemMasters.action';
import { fetchSeason } from '../../../Library/BomLibrary/store/BomLibrary.action';
import { fetchAllProductType } from '../../../RangePlan/ProductList/store/ProductList.action';
import { getCompanyUsers } from '../TodoCommon.action';
import type {
  CostingDispatch,
  CostingFilterKey,
  CostingFilterValues,
  CostingListParams,
  CostingRootState,
  CostingSelectOption,
  ProductCostingFilterProps,
} from './store/Costing.type';

interface FilterOptionsState {
  supplierOptions: CostingSelectOption[];
  colourwayOptions: CostingSelectOption[];
  statusOptions: CostingSelectOption[];
  brandOptions: CostingSelectOption[];
  subBrandOptions: CostingSelectOption[];
  incotermsOptions: CostingSelectOption[];
  currencyOptions: CostingSelectOption[];
  yearOptions: CostingSelectOption[];
  monthOptions: CostingSelectOption[];
  seasonsOptions: CostingSelectOption[];
  productTypeOptions: CostingSelectOption[];
  category1Options: CostingSelectOption[];
  category2Options: CostingSelectOption[];
  category3Options: CostingSelectOption[];
  category4Options: CostingSelectOption[];
  companyUserOptions: CostingSelectOption[];
  designerOptions: CostingSelectOption[];
}

interface FilterSelectProps {
  /** Form field name, kept from the previous control for autofill/testing parity. */
  name?: string;
  options: CostingSelectOption[];
  value: CostingSelectOption[];
  /** Shown when nothing is picked, and as the Select All row's label. */
  placeholder: string;
  onChange: (_selected: CostingSelectOption[]) => void;
  /** Label of the option the menu opens scrolled to while nothing is picked. */
  scrollToLabel?: string;
}

/**
 * One dropdown in the filter panel, on the master-design `NewSelectMultiSelect`.
 *
 * Wrapped rather than repeated because the panel holds twenty-one of these and every one wants the
 * same behaviour — multi-select, an in-menu search and a Select All row. The previous control was
 * `react-multi-select-component`, which is not part of the master-design set.
 *
 * `name` is spread in rather than passed directly: `exactOptionalPropertyTypes` is on, so handing
 * an optional prop an explicit `undefined` is a type error.
 */
function FilterSelect({ name, options, value, placeholder, onChange, scrollToLabel }: FilterSelectProps) {
  return (
    <NewSelectMultiSelect<CostingSelectOption>
      isMulti
      showSearch
      {...(name ? { name } : {})}
      {...(scrollToLabel ? { scrollToLabel } : {})}
      options={options ?? []}
      value={value ?? []}
      placeholder={placeholder}
      selectAllLabel={placeholder}
      searchPlaceholder={placeholder}
      onChange={(selected) => onChange((selected as CostingSelectOption[]) ?? [])}
    />
  );
}

/** Filter popover for the Costing TODO list: 21 multi-selects plus apply/clear. */
function ProductCostingFilter({ activeCostingTemplate }: ProductCostingFilterProps) {
  const { t: translate } = useTranslation('common');
  const { t: translateReports } = useTranslation('todo-reports');
  const t = translate as unknown as TranslateFn;
  const tReports = translateReports as unknown as TranslateFn;

  const [state, setState] = useState<FilterOptionsState>({
    supplierOptions: [],
    colourwayOptions: [],
    statusOptions: [],
    brandOptions: [],
    subBrandOptions: [],
    incotermsOptions: [],
    currencyOptions: currencyCodes.map((currKey: string) => convertSelectLabel(currKey, true)) as CostingSelectOption[],
    yearOptions: createOptions(getYears(), 'name', '_id') as CostingSelectOption[],
    monthOptions: createOptions(months, 'name', '_id') as CostingSelectOption[],
    seasonsOptions: [],
    productTypeOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    companyUserOptions: [],
    designerOptions: [],
  });

  const {
    ColorLibraryDetails: { ColorLibList },
    CostingTodoListData: {
      costingFilterValues: {
        searchText,
        sortOrder,
        sortPropertyName,
        supplier = [],
        colourway = [],
        currency = [],
        incoterms = [],
        selectedcosting_Status,
        selectedBrand = [],
        selectedSubBrand = [],
        selectedSeason = [],
        selectedYear = [],
        selectedMonth = [],
        selectedProductType = [],
        selectedCat1 = [],
        selectedCat2 = [],
        selectedCat3 = [],
        selectedCat4 = [],
        selectedCompanyUser = [],
        selectedDesigner = [],
        selectedCoo = [],
        productGroupId = null,
        selectedLicensor = [],
        selectedLicense = [],
        selectedLikeness = [],
      },
    },
    SystemMastersDetails: { multiSubBrandList, licensorList, licenseList, likenessList },
    UserDetails: {
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
      userInfo: { userId },
    },
  } = useSelector((reduxState: CostingRootState) => reduxState);

  const dispatch = useDispatch<CostingDispatch>();
  const [filterUpdateTrigger, setFilterUpdateTrigger] = useState(0);
  const statusRestoredRef = useRef(false);
  const {
    supplierOptions,
    colourwayOptions,
    incotermsOptions,
    currencyOptions,
    statusOptions,
    brandOptions,
    subBrandOptions,
    yearOptions,
    monthOptions,
    seasonsOptions,
    productTypeOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    companyUserOptions,
  } = state;

  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = (await dispatch(getSubBrandAccess())) as { data?: boolean } | undefined;
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = (await dispatch(getLicensorAccess()).catch(() => null)) as { isActiveLicensor?: boolean } | null;
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('costingTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved) as Partial<CostingFilterValues> | null;
      if ((parsed?.selectedLicensor?.length ?? 0) > 0 || (parsed?.selectedLicense?.length ?? 0) > 0 || (parsed?.selectedLikeness?.length ?? 0) > 0) {
        dispatch(
          filterSortData({
            selectedLicensor: parsed?.selectedLicensor || [],
            selectedLicense: parsed?.selectedLicense || [],
            selectedLikeness: parsed?.selectedLikeness || [],
          }),
        );
      }
      if ((parsed?.selectedLicensor?.length ?? 0) > 0) {
        dispatch(
          getLicenseDetails({
            licensorIds: parsed?.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if ((parsed?.selectedLicensor?.length ?? 0) > 0 && (parsed?.selectedLicense?.length ?? 0) > 0) {
        dispatch(
          getLikenessDetails({
            licensorIds: parsed?.selectedLicensor,
            licenseIds: parsed?.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      console.log('error', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    if (subBrandAccess === false) {
      const currentFilter = JSON.parse(localStorage.getItem('costingTodoListFilter') || '{}') as Partial<CostingFilterValues>;
      if (currentFilter.selectedSubBrand) {
        delete currentFilter.selectedSubBrand;
        localStorage.setItem('costingTodoListFilter', JSON.stringify(currentFilter));

        dispatch(filterSortData({ selectedSubBrand: [] }));
      }
    }
  }, [subBrandAccess, dispatch]);

  useEffect(() => {
    if (!statusRestoredRef.current && statusOptions.length > 0) {
      const isTrue = JSON.parse(localStorage.getItem('costingTodoListFilter') ?? 'null') as Partial<CostingFilterValues> | null;
      if (isTrue) {
        let statusArray: string[] = [];
        if (Array.isArray(isTrue.selectedcosting_Status) && isTrue.selectedcosting_Status.length > 0) {
          statusArray = isTrue.selectedcosting_Status;
        } else if (typeof isTrue.selectedcosting_Status === 'string' && isTrue.selectedcosting_Status) {
          statusArray = [isTrue.selectedcosting_Status];
        }
        if (statusArray.length > 0 && (!Array.isArray(selectedcosting_Status) || selectedcosting_Status.length === 0)) {
          dispatch(filterSortData({ selectedcosting_Status: statusArray }));
        }
      }
      statusRestoredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions.length, dispatch]);

  useEffect(() => {
    if (Array.isArray(ColorLibList) && ColorLibList.length > 0) {
      const copyState = cloneData(state);
      const cloneColourwayOptions = ColorLibList.map((val) => {
        return { ...val, value: val.name, label: val.name };
      });
      copyState.colourwayOptions = cloneColourwayOptions;
      setState((prevState) => ({
        ...prevState,
        ...copyState,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ColorLibList]);

  useEffect(() => {
    if (subBrandAccess && checkArray(selectedBrand)) {
      dispatch(
        getMultiSubBrandDetails({
          brandId: selectedBrand,
        }),
      );
    }
  }, [dispatch, subBrandAccess, selectedBrand]);

  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id') as CostingSelectOption[];
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  /** Loads the option list for each requested category tier. */
  const setCategoryList = (catArray: number[]) => {
    catArray?.forEach(async (type) => {
      const res = (await dispatch(
        getTypeBasedCatgeory({
          categoryType: type,
          loggedInUserId: userId,
        }),
      )) as { code?: number; data?: unknown } | undefined;
      if (res?.code === 200) {
        const categoryList = createOptions(res?.data, 'name', '_id') as CostingSelectOption[];
        setState(
          (prev) =>
            ({
              ...prev,
              [`category${type}Options`]: categoryList,
            } as FilterOptionsState),
        );
      }
    });
  };

  useEffect(() => {
    const callFun = async () => {
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrands(userId as string))
        : await dispatch(
            fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );

      if (checkArray(BrandRes)) {
        const brand_Options = createOptions(BrandRes, 'name', '_id') as CostingSelectOption[];
        setState((prevState) => ({
          ...prevState,
          brandOptions: brand_Options,
        }));
      }

      const seasonsRes = (await dispatch(fetchSeason({ loggedInUserId: userId }))) as { payload?: unknown } | undefined;
      if (seasonsRes?.payload) {
        const seasonsOpt = createOptions(seasonsRes.payload, 'name', '_id') as CostingSelectOption[];
        setState((prevState) => ({
          ...prevState,
          seasonsOptions: seasonsOpt,
        }));
      }

      const colourWaysList = await dispatch(fetchColorLibrary({ loggedInUserId: userId }));
      if (checkArray(colourWaysList)) {
        const _colourwayOptions = createOptions(colourWaysList, 'name', '_id') as CostingSelectOption[];
        setState((prevState) => ({
          ...prevState,
          colourwayOptions: _colourwayOptions,
        }));
      }

      if (!isSupplierLoggedIn) {
        let supp_list: Array<Record<string, unknown>> = [];
        if (isCompanyUserLoggedIn) {
          const userSuppliers = (await dispatch(getUserSuppliers())) as { suppliers?: Array<Record<string, unknown>> } | undefined;
          const suppliers = userSuppliers?.suppliers as Array<{ name?: string | undefined; companies?: Array<{ supplierName?: string }> }> | undefined;
          supp_list = (
            suppliers?.length
              ? suppliers.map((ele) => {
                  if (ele.companies?.length) {
                    ele.name = ele.companies[0]?.supplierName;
                  }
                  return ele;
                })
              : []
          ) as Array<Record<string, unknown>>;
        } else {
          supp_list = (await dispatch(
            getSuppliers({
              loggedInUserId: userId,
            }),
          )) as Array<Record<string, unknown>>;
        }
        if (checkArray(supp_list)) {
          const _supplierOptions = createOptions(supp_list, 'name', '_id') as CostingSelectOption[];
          setState((prevState) => ({
            ...prevState,
            supplierOptions: _supplierOptions,
          }));
        }
      }

      const statusArr = createOptions(['Draft', 'Pending', 'Partially Complete', 'Complete']) as CostingSelectOption[];
      if (statusArr) {
        setState((prevState) => ({
          ...prevState,
          statusOptions: statusArr,
        }));
      }
      if (!isSupplierLoggedIn) {
        const companyUsersRes = (await dispatch(getCompanyUsers())) as
          | { code?: number; data?: Array<{ _id?: string; firstName?: string; lastName?: string }> }
          | Array<{ _id?: string; firstName?: string; lastName?: string }>
          | undefined;
        const asEnvelope = companyUsersRes as { code?: number; data?: Array<{ _id?: string; firstName?: string; lastName?: string }> } | undefined;
        if (asEnvelope?.code === 200 || checkArray(companyUsersRes)) {
          const companyUser = (asEnvelope?.data || (companyUsersRes as Array<{ _id?: string; firstName?: string; lastName?: string }>))?.map((user) => {
            return {
              ...user,
              label: `${user?.firstName} ${user?.lastName}`,
              value: user?._id,
            };
          }) as CostingSelectOption[];
          setState((prev) => ({ ...prev, companyUserOptions: companyUser }));
        }
      } else {
        setState((prev) => ({ ...prev, companyUserOptions: [] }));
      }

      const productTypeRes = (await dispatch(
        fetchAllProductType({
          loggedInUserId: userId,
          ...(catBasedOnPTData ? { tier: 'Tier1' } : {}),
        }),
      )) as { code?: number; data?: unknown } | undefined;
      if (productTypeRes?.code === 200) {
        const productTypes = createOptions(productTypeRes?.data, 'name', '_id') as CostingSelectOption[];
        setState((prev) => ({ ...prev, productTypeOptions: productTypes }));
      }
      const getIncotermsRes = (await dispatch(getIncotermsDetails())) as { code?: number; data?: unknown } | undefined;
      if (getIncotermsRes?.code === 200) {
        const incotermsOpts = createOptions(getIncotermsRes?.data, 'name', '_id') as CostingSelectOption[];
        setState((prev) => ({ ...prev, incotermsOptions: incotermsOpts }));
      }
    };

    callFun();
  }, [dispatch, userId, catBasedOnPTData, isSupplierLoggedIn]);

  /**
   * Writes one filter key to the store, clearing the downstream keys that depend on it.
   * The sub-brand branch reads `obj.selectedSubBrand`, which is always undefined because `obj`
   * only ever holds the key just set — preserved verbatim from the JavaScript (see B2).
   */
  const handleInputChange = async (value: string[], name: CostingFilterKey) => {
    const obj = { [name]: value } as Partial<CostingFilterValues>;

    if (name === 'selectedProductType' && catBasedOnPTData) {
      obj.selectedCat1 = [];
      obj.selectedCat2 = [];
      obj.selectedCat3 = [];
      obj.selectedCat4 = [];
    }

    if (['selectedCat2', 'selectedCat1'].includes(name)) {
      obj.selectedCat3 = [];
      obj.selectedCat4 = [];
    }
    if (['selectedCat3'].includes(name)) {
      obj.selectedCat4 = [];
    }

    if (name === 'selectedBrand') {
      const validSubBrands =
        obj.selectedSubBrand?.filter((subBrandId) => {
          const subBrand = multiSubBrandList?.find((sb) => sb._id === subBrandId);
          return subBrand && value?.includes(subBrand.brandId as string);
        }) || [];
      obj.selectedSubBrand = validSubBrands;
    }

    await dispatch(filterSortData(obj));
  };

  const licensorOptions = useMemo(
    () => (checkArray(licensorList) ? (createOptions(licensorList, 'name', '_id') as CostingSelectOption[]) : []),
    [licensorList],
  );
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? (createOptions(licenseList, 'name', '_id') as CostingSelectOption[]) : []), [licenseList]);
  const likenessOptions = useMemo(
    () => (checkArray(likenessList) ? (createOptions(likenessList, 'name', '_id') as CostingSelectOption[]) : []),
    [likenessList],
  );

  /** Licensor → license → likeness cascade; each level clears the levels beneath it. */
  const handleFilterLicense = (val: string[], key: CostingFilterKey) => {
    const newData = { [key]: val } as Partial<CostingFilterValues>;
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(clearLikenessData());
      if (val.length > 0) {
        dispatch(getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(filterSortData(newData));
  };

  const COOOptionsList = useMemo<CostingSelectOption[]>(() => {
    if (Array.isArray(CountriesList)) {
      return (CountriesList as string[]).map((cou) => {
        return { label: cou, value: cou };
      });
    }
    return [];
  }, []);

  /** Resets every filter key, drops the persisted payload and refetches the unfiltered list. */
  const clearAll = async () => {
    try {
      const loadingObj = {
        loading: true,
      };
      await dispatch(filterSortData(loadingObj));

      const sortinObj: Partial<CostingFilterValues> = {
        supplier: [],
        colourway: [],
        currency: [],
        incoterms: [],
        sortOrder,
        sortPropertyName,
        searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        selectedcosting_Status: [],
        selectedBrand: [],
        ...(subBrandAccess ? { selectedSubBrand: [] } : {}),
        selectedSeason: [],
        selectedYear: [],
        selectedMonth: [],
        selectedProductType: [],
        selectedCat1: [],
        selectedCat2: [],
        selectedCat3: [],
        selectedCat4: [],
        selectedCompanyUser: [],
        selectedDesigner: [],
        selectedCoo: [],
        productGroupId: null,
        selectedLicensor: [],
        selectedLicense: [],
        selectedLikeness: [],
        costingTemplateId: activeCostingTemplate,
      };
      const sortinObj1: CostingListParams = {
        supplier: [],
        colourway: [],
        currency: [],
        incoterms: [],
        status: [],
        type: 'costing',
        brand: [],
        ...(subBrandAccess ? { subBrand: [] } : {}),
        season: [],
        year: [],
        month: [],
        productType: [],
        catOne: [],
        catTwo: [],
        catThree: [],
        catFour: [],
        createdBy: [],
        designer: [],
        COO: [],
        productGroupId: null,
        licensorId: [],
        licenseId: [],
        likenessId: [],
        pageIndex: 1,
        pageSize: Page_Size,
        costingTemplateId: activeCostingTemplate,
      };

      if (isSupplierLoggedIn) sortinObj1.supplierId = userId;
      dispatch(clearLicenseData());
      dispatch(clearLikenessData());
      const res = await dispatch(getCostingList(sortinObj1));

      dispatch(getCostingStats(sortinObj1));

      localStorage.removeItem('costingTodoListFilter');
      setFilterUpdateTrigger((prev) => prev + 1);

      if (res) {
        const finalObj = {
          ...sortinObj,
          loading: false,
        };
        await dispatch(filterSortData(finalObj));
      }
    } catch (error) {
      const errorObj = {
        loading: false,
      };
      await dispatch(filterSortData(errorObj));
    }
  };

  /** Persists the current selections, refetches the list and stats, then clears the loader. */
  const applyFilter = async () => {
    try {
      const loadingObj = {
        loading: true,
      };
      await dispatch(filterSortData(loadingObj));

      const params: CostingListParams = {
        type: 'costing',
        sortOrder,
        sortPropertyName,
        searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        supplier,
        colourway,
        currency,
        incoterms,
        status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
        brand: selectedBrand,
        season: selectedSeason,
        year: selectedYear,
        month: selectedMonth,
        productType: selectedProductType,
        catOne: selectedCat1,
        catTwo: selectedCat2,
        catThree: selectedCat3,
        catFour: selectedCat4,
        createdBy: selectedCompanyUser,
        designer: selectedDesigner,
        COO: selectedCoo,
        productGroupId,
        licensorId: selectedLicensor,
        licenseId: selectedLicense,
        likenessId: selectedLikeness,
        ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
        costingTemplateId: activeCostingTemplate,
      };
      const obj: Partial<CostingFilterValues> = {
        supplier,
        colourway,
        currency,
        incoterms,
        selectedcosting_Status,
        selectedBrand,
        selectedSeason,
        selectedYear,
        selectedMonth,
        selectedProductType,
        selectedCat1,
        selectedCat2,
        selectedCat3,
        selectedCat4,
        selectedCompanyUser,
        selectedDesigner,
        selectedCoo,
        productGroupId,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
        costingTemplateId: activeCostingTemplate,
        ...(subBrandAccess ? { selectedSubBrand } : {}),
      };

      localStorage.setItem('costingTodoListFilter', JSON.stringify(obj));
      setFilterUpdateTrigger((prev) => prev + 1);

      if (isSupplierLoggedIn) params.supplierId = userId;

      const res = await dispatch(getCostingList(params));

      dispatch(getCostingStats(params));
      await dispatch(filterSortData(obj));

      if (res) {
        const cloneObj = {
          loading: false,
          pageIndex: 1,
          pageSize: Page_Size,
        };
        await dispatch(filterSortData(cloneObj));
      }
    } catch (error) {
      const errorObj = {
        loading: false,
      };
      await dispatch(filterSortData(errorObj));
    }
  };

  /** Category option lists are loaded lazily, only once the popover opens. */
  const callDropDownApi = (boolState: boolean) => {
    if (boolState) {
      if (!isEmpty(userId)) {
        setCategoryList([1, 2, 3, 4]);
      }
    }
  };

  const category3OptionsMemoize = useMemo(() => {
    if (selectedCat1.length > 0 && selectedCat2.length > 0) {
      return category3Options?.filter((val) => selectedCat1.includes(val.categoryOneId as string) && selectedCat2.includes(val.categoryTwoId as string));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat1?.length, selectedCat2?.length]);

  const category4OptionsMemoize = useMemo(() => {
    if (selectedCat1.length > 0 && selectedCat2.length > 0 && selectedCat3.length > 0) {
      return category4Options?.filter(
        (val) =>
          selectedCat1.includes(val.categoryOneId as string) &&
          selectedCat2.includes(val.categoryTwoId as string) &&
          selectedCat3.includes(val.categoryThreeId as string),
      );
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat1?.length, selectedCat2?.length, selectedCat3?.length]);

  const getIds = (arr: unknown): string[] => (Array.isArray(arr) ? arr.filter(Boolean).map((v) => String(v)) : []);
  const selectedPTIds = getIds(selectedProductType);
  const selectedCat1Ids = getIds(selectedCat1);
  const selectedCat2Ids = getIds(selectedCat2);
  const selectedCat3Ids = getIds(selectedCat3);

  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(String(id))) || pts?.some((id) => selectedPTIds.includes(String(id)));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1.map((v) => String(v)) : [];
      return usedCat1?.some((id) => selectedCat1Ids.includes(String(id)));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3OptionsMemoize?.length ? category3OptionsMemoize : category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = (category3OptionsMemoize?.length ? category3OptionsMemoize : category3Options) || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids.includes(String(opt?.categoryOneId)) && selectedCat2Ids.includes(String(opt?.categoryTwoId)));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, category3OptionsMemoize, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4OptionsMemoize?.length ? category4OptionsMemoize : category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = (category4OptionsMemoize?.length ? category4OptionsMemoize : category4Options) || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) =>
        selectedCat1Ids.includes(String(opt?.categoryOneId)) &&
        selectedCat2Ids.includes(String(opt?.categoryTwoId)) &&
        selectedCat3Ids.includes(String(opt?.categoryThreeId)),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, category4OptionsMemoize, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  /** Drives the "filter applied" highlight on the trigger button, read back from localStorage. */
  const filterApplied = useMemo(() => {
    const localStorageFilter = JSON.parse(localStorage.getItem('costingTodoListFilter') || '{}') as Record<string, unknown>;

    const hasValue = (value: unknown): boolean => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      if (typeof value === 'number') {
        return true;
      }
      return value !== null && value !== undefined;
    };

    return (
      hasValue(localStorageFilter.supplier) ||
      hasValue(localStorageFilter.colourway) ||
      hasValue(localStorageFilter.currency) ||
      hasValue(localStorageFilter.incoterms) ||
      hasValue(localStorageFilter.selectedcosting_Status) ||
      hasValue(localStorageFilter.selectedBrand) ||
      hasValue(localStorageFilter.selectedSubBrand) ||
      hasValue(localStorageFilter.selectedSeason) ||
      hasValue(localStorageFilter.selectedYear) ||
      hasValue(localStorageFilter.selectedMonth) ||
      hasValue(localStorageFilter.selectedProductType) ||
      hasValue(localStorageFilter.selectedCat1) ||
      hasValue(localStorageFilter.selectedCat2) ||
      hasValue(localStorageFilter.selectedCat3) ||
      hasValue(localStorageFilter.selectedCat4) ||
      hasValue(localStorageFilter.selectedCompanyUser) ||
      hasValue(localStorageFilter.selectedDesigner) ||
      hasValue(localStorageFilter.selectedCoo) ||
      hasValue(localStorageFilter.productGroupId) ||
      hasValue(localStorageFilter.selectedLicensor) ||
      hasValue(localStorageFilter.selectedLicense) ||
      hasValue(localStorageFilter.selectedLikeness)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUpdateTrigger]);

  const isApply = filterApplied;
  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER.FILTER')}>
      {/** master-design "filter applied" state: the same icon button, `.active` locking the blue. */}
      <button type="button" aria-label={t('FILTER.SORT')} className={`bombiix-icon-container${isApply ? ' active' : ''}`}>
        <OverlayTrigger
          placement="bottom"
          trigger="click"
          rootClose
          onToggle={(show: boolean) => callDropDownApi(show)}
          overlay={
            <Popover id="costing-todo-filter-popover" className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="brand"
                        options={brandOptions}
                        value={brandOptions?.filter((val) => selectedBrand?.includes(val?._id as string)) ?? []}
                        placeholder={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedBrand',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  {subBrandAccess && (
                    <Col md={2}>
                      <FormGroup>
                        <FilterSelect
                          name="subbrand"
                          options={subBrandOptions}
                          value={subBrandOptions?.filter((val) => selectedSubBrand?.includes(val?._id as string)) ?? []}
                          placeholder="Select Sub Brand"
                          onChange={(selected) =>
                            handleInputChange(
                              selected.map((val) => val._id as string),
                              'selectedSubBrand',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="season"
                        options={seasonsOptions || []}
                        value={seasonsOptions?.filter((val) => selectedSeason?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedSeason',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="year"
                        scrollToLabel={getCurrentYearLabel()}
                        options={yearOptions || []}
                        value={yearOptions?.filter((val) => selectedYear?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_YEAR')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedYear',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="month"
                        options={monthOptions || []}
                        value={monthOptions?.filter((val) => selectedMonth?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_MONTH')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedMonth',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="productType"
                        options={productTypeOptions || []}
                        value={productTypeOptions?.filter((val) => selectedProductType?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_PRODUCT_TYPE')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedProductType',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options || []}
                        value={(catBasedOnPTData ? category1Filtered : category1Options)?.filter((val) => selectedCat1?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedCat1',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options || []}
                        value={(catBasedOnPTData ? category2Filtered : category2Options)?.filter((val) => selectedCat2?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedCat2',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3OptionsMemoize || []}
                        value={
                          (catBasedOnPTData ? category3Filtered : category3OptionsMemoize || [])?.filter((val) => selectedCat3?.includes(val?._id as string)) ??
                          []
                        }
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_CATEGORY_3')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedCat3',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4OptionsMemoize || []}
                        value={
                          (catBasedOnPTData ? category4Filtered : category4OptionsMemoize || [])?.filter((val) => selectedCat4?.includes(val?._id as string)) ??
                          []
                        }
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedCat4',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="colorway"
                        options={colourwayOptions}
                        value={colourwayOptions?.filter((val) => colourway?.includes(val?._id as string)) ?? []}
                        placeholder={t('COSTING_TO_DO_LIST.SELECT_COLORWAY')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'colourway',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  {!isSupplierLoggedIn && (
                    <Col md={2}>
                      <FormGroup>
                        <FilterSelect
                          name="supplier"
                          options={supplierOptions}
                          value={supplierOptions?.filter((val) => supplier?.includes(val?._id as string)) ?? []}
                          placeholder={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleInputChange(
                              selected.map((val) => val._id as string),
                              'supplier',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                  )}

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="createdBy"
                        options={companyUserOptions || []}
                        value={companyUserOptions?.filter((val) => selectedCompanyUser?.includes(val?._id as string)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.CREATED')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val._id as string),
                            'selectedCompanyUser',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="coo"
                        options={COOOptionsList || []}
                        value={COOOptionsList?.filter((val) => selectedCoo?.includes(val?.value)) ?? []}
                        placeholder={t('RANGE_PLANS.PRODUCT_LIST.FILTER_PRODUCT_LIST.COUNTRY_OF_ORIGIN_LABEL')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val.value),
                            'selectedCoo',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="currency"
                        options={currencyOptions}
                        value={currencyOptions?.filter((val) => currency?.includes(val?.value)) ?? []}
                        placeholder={t('COSTING_TO_DO_LIST.SELECT_CURENCY')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val.value),
                            'currency',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="incoterms"
                        options={incotermsOptions}
                        value={incotermsOptions?.filter((val) => incoterms?.includes(val?.value)) ?? []}
                        placeholder={t('COSTING_TO_DO_LIST.SELECT_INTO')}
                        onChange={(selected) =>
                          handleInputChange(
                            selected.map((val) => val.value),
                            'incoterms',
                          )
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={2}>
                    <FormGroup>
                      <FilterSelect
                        name="status"
                        options={statusOptions || []}
                        value={
                          statusOptions?.filter((val) =>
                            (Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [])?.includes(
                              val.value,
                            ),
                          ) || []
                        }
                        placeholder={t('COSTING_TO_DO_LIST.STATUSS')}
                        onChange={(selected) => {
                          handleInputChange(
                            selected.map((val) => val.value),
                            'selectedcosting_Status',
                          );
                        }}
                      />
                    </FormGroup>
                  </Col>

                  {licensorAccess && (
                    <>
                      <Col md={2}>
                        <FormGroup>
                          <FilterSelect
                            options={licensorOptions}
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id as string))}
                            placeholder={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id as string),
                                'selectedLicensor',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <FilterSelect
                            options={licenseOptions}
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id as string))}
                            placeholder={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id as string),
                                'selectedLicense',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <FilterSelect
                            options={likenessOptions}
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id as string))}
                            placeholder={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id as string),
                                'selectedLikeness',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                    </>
                  )}
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.CLEAR')}>
                        <button onClick={clearAll} aria-label="Close" className="bombiix-icon-container bombiix-icon-container--red" type="button">
                          <ClearIcon />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.APPLY')}>
                        <button onClick={applyFilter} aria-label="Apply" className="bombiix-icon-container  ml-16" type="button">
                          <SaveIcon />
                        </button>
                      </CustomTooltip>
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          {/** A span, not the icon itself: OverlayTrigger clones its child to attach the click
               handler and a ref, which needs a DOM element rather than a function component. */}
          <span className="d-inline-flex">
            <FilterIcon />
          </span>
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default ProductCostingFilter;
